import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { PersonalNotificationListItemBase } from './personal-notification-list-item-base';
import { PersonalNotificationsCategoryType } from '@fi-shared/types/personal-notifications-category-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiPersonalNotificationClickAction } from '@fi-app/personal-notifications/personal-notification-click-action';
import { PersonalNotificationsService } from '@fi-app/personal-notifications/personal-notifications.service';
import { IPersonalNotificationsItem } from '@fi-app/personal-notifications/personal-notifications';
import { FiPersonalNotificationProfileType } from '@fi-app/personal-notifications/fi-personal-notification-enums';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiDateFormatOptions, FiDateTimeFormatOptions } from '@fi-shared/utils';

@Component({
    selector: 'fi-personal-notifications-list-item',
    template: `
        <a
            class="personal-notification-item-inner LightBackgroundColorHover"
            [class.visited]="isVisited()"
            (click)="personalNotificationClick()"
        >
            <div
                class="item-icon"
                [ngClass]="{
                    'business-unit-no-profile': isBusinessUnitNoProfile(),
                    'module-profile': isModuleProfile(),
                }"
            >
                <fi-profile-icon
                    [profileData]="getFromProfile()"
                    [profilePictureIcon]="getProfileIcon()"
                    [hideConnectedInfo]="isConnectedInfoHidden()"
                >
                </fi-profile-icon>
            </div>

            <div class="item-text">
                <div [innerHtml]="getPersonalNotificationText()"></div>
                <div class="BrandedColor">
                    {{ getPersonalNotificationDate() }}
                </div>
            </div>
        </a>
    `,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./personal-notifications-list-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-personal-notifications-list-item' },
    standalone: false,
})
export class PersonalNotificationsListItemComponent extends PersonalNotificationListItemBase {
    @Input() listItem: IPersonalNotificationsItem;
    @Output() afterPersonalNotificationClick = new EventEmitter<number>();

    constructor(
        private fiPersonalNotificationsService: PersonalNotificationsService,
        private fiStateService: FiStateService,
        private fiFormatDateService: FiFormatDateService,
        injector: Injector,
    ) {
        super(injector);
    }

    protected getFromProfile() {
        return this.listItem.FromProfile;
    }

    private isBusinessUnitProfile(): boolean {
        return this.listItem.ProfileType === FiPersonalNotificationProfileType.BusinessUnit;
    }

    protected isTaskModuleProfile(): boolean {
        return [
            FiPersonalNotificationProfileType.TaskChecklistAutomated,
            FiPersonalNotificationProfileType.TaskSystemDeadline,
        ].includes(this.listItem.ProfileType);
    }

    protected isConnectedInfoHidden(): boolean {
        return this.isBusinessUnitProfile();
    }

    protected isBusinessUnitNoProfile(): boolean {
        const businessUnitNoProfile =
            this.isBusinessUnitProfile() && !this.listItem.FromProfile.MediaBankPhysicalFileName;
        return businessUnitNoProfile;
    }

    protected isOnboardingReminderProfile(): boolean {
        return [FiPersonalNotificationProfileType.OnboardingReminder].includes(this.listItem.ProfileType);
    }

    protected isModuleProfile(): boolean {
        return this.isTaskModuleProfile() || this.isOnboardingReminderProfile();
    }

    protected getProfileIcon(): string {
        if (this.isBusinessUnitProfile()) {
            return 'fa-solid fa-shop business-unit-icon';
        }
        if (this.isTaskModuleProfile()) {
            return 'fa-clipboard-check fa-regular checklist-automated-icon';
        }
        if (this.isOnboardingReminderProfile()) {
            return 'fa-file-certificate fa-regular';
        }
        return 'fa-user-circle fa-regular';
    }

    protected personalNotificationClick() {
        const clickAction = this.listItem.StateDetails.ClickAction;
        if (clickAction === FiPersonalNotificationClickAction.GoToState) {
            this.fiStateService.go(this.listItem.StateDetails.State, this.listItem.StateDetails.StateParams);
        } else if (clickAction === FiPersonalNotificationClickAction.OpenChatConversation) {
            this.handleChatConversationItemClick(this.listItem);
        }

        const categoryType = this.listItem.CategoryType;
        if (this.listItem.CategoryType !== PersonalNotificationsCategoryType.Visited) {
            this.listItem.CategoryType = PersonalNotificationsCategoryType.Visited;

            this.fiPersonalNotificationsService.markPersonalNotificationsAsVisited(
                this.listItem.NotificationItems.map(function (item) {
                    return item.Id;
                }),
            );
        }
        this.afterPersonalNotificationClick.emit(categoryType);
    }

    protected getPersonalNotificationText(): string {
        const text = this.listItem?.StateDetails.Text;
        return text;
    }

    protected getPersonalNotificationDate(): string {
        return this.fiFormatDateService.formatDate(this.listItem.CreatedOn, FiDateTimeFormatOptions, {
            showToday: false,
        });
    }

    protected isVisited() {
        return this.listItem.CategoryType == PersonalNotificationsCategoryType.Visited;
    }
}
